/**
 * The Ramadan day lists (owner 2026-10-31, spec 09): one iftar (and sehri) list per Ramadan day, grouped by day with the
 * counts, and a day's people with check-in at the door — the same rules and the same look as an event's list in
 * «Påmelding». Shown on the «Ramadan» page and in «Påmelding». The server says in every answer what this person may do
 * (`can`): counts for events.write, names for the admin-tier permissions, «Sjekk inn» for events.checkin.
 */
import React, { useState } from 'react';
import { post, send, errText, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { shortDate } from '../lib/format';
import { Btn, Card, LoadError, Loading, Msg, Tag } from '../components/ui';
import { MoreButton, usePaged } from './work-ui';

const KIND_NO: Record<string, string> = { iftar: 'Iftar', sehri: 'Sehri' };
const seats = (n: number) => `${n} ${n === 1 ? 'plass' : 'plasser'}`;

/** One day's counts in words — never a name. */
export function DayCounts({ s }: { s: Body }) {
  return (
    <span className="reg-counts">
      <span>{seats(s.registeredSeats)} påmeldt</span>
      {s.waitlistedEntries > 0 && <span>· {s.waitlistedEntries} venter</span>}
      {s.checkedInSeats > 0 && <span>· {s.checkedInSeats} kommet</span>}
      {s.capacity !== null && <span>· {s.remaining} ledige av {s.capacity}</span>}
    </span>
  );
}

/** Group the lists by day: [{ day, items: [{list, summary}] }], in day order. */
export function byDay(items: Body[]): { day: string; items: Body[] }[] {
  const out: { day: string; items: Body[] }[] = [];
  for (const x of items) {
    const last = out[out.length - 1];
    if (last && last.day === x.list.day) last.items.push(x); else out.push({ day: x.list.day, items: [x] });
  }
  return out;
}

export function RamadanLists({ year, reloadKey = 0 }: { year?: number | null; reloadKey?: number }) {
  const data = useLoad<Body>(`/ramadan/lists${year ? `?year=${year}` : ''}`, [reloadKey]);
  const [open, setOpen] = useState<string | null>(null);
  if (data.error) return <LoadError error={data.error} retry={data.reload} />;
  if (!data.data) return <Loading />;
  const d = data.data;
  const groups = byDay(d.items || []);
  const today: string = d.today;
  return (
    <div className="card-body">
      {d.totals && d.totals.lists > 0 && (
        <ul className="reg-tiles" aria-label="Alle ramadan-listene">
          <li><b>{d.totals.registeredSeats}</b><span>plasser påmeldt · {d.totals.lists} lister</span></li>
          {d.totals.waitlistedEntries > 0 && <li><b>{d.totals.waitlistedEntries}</b><span>på venteliste</span></li>}
          {d.totals.checkedInSeats > 0 && <li><b>{d.totals.checkedInSeats}</b><span>kommet</span></li>}
        </ul>
      )}
      {!groups.length && <p className="work-empty">Ingen lister for ramadan {d.year} ennå.</p>}
      <ul className="work-rows">{groups.map((g) => (
        <li key={g.day}>
          <div className="work-row-meta" style={{ padding: '8px 0 2px' }}>
            <b>{shortDate(g.day)}</b>{g.day === today && <Tag tone="green">I dag</Tag>}
          </div>
          {g.items.map((x) => (
            <button key={x.list.id} type="button" className={'work-row' + (open === x.list.id ? ' sel' : '')} aria-expanded={open === x.list.id}
                    onClick={() => setOpen(open === x.list.id ? null : x.list.id)}>
              <span className="work-row-title">{KIND_NO[x.list.kind] || x.list.kind}</span>
              <span className="work-row-meta">
                {x.list.state !== 'open' && <Tag tone="grey">{x.list.state === 'closed' ? 'Stengt' : 'Avlyst'}</Tag>}
                <DayCounts s={x.summary} />
              </span>
            </button>
          ))}
          {g.items.some((x) => x.list.id === open) && d.can && d.can.names && <DayPeople id={open!} onChanged={() => void data.reload()} />}
          {g.items.some((x) => x.list.id === open) && !(d.can && d.can.names) && <p className="note">Du ser antall. Navnene ser de som har tilgangen «Påmeldingslister» eller «Innsjekking».</p>}
        </li>
      ))}</ul>
    </div>
  );
}

/** A day's people, with «Sjekk inn» / «Angre» as at an event's door. */
function DayPeople({ id, onChanged }: { id: string; onChanged: () => void }) {
  const base = `/ramadan/lists/${encodeURIComponent(id)}/registrations`;
  const page = usePaged(`${base}?status=active`, (d) => d.registrations);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [msg, say] = useMsg();
  const checkIn = async (e: Body, checkedIn: boolean) => {
    setPending((p) => ({ ...p, [e.id]: checkedIn }));
    const r = await send(`${base}/${encodeURIComponent(e.id)}/checkin`, { method: 'PUT', body: JSON.stringify({ checkedIn }) });
    setPending((p) => { const n = { ...p }; delete n[e.id]; return n; });
    if (!r.ok) return say(errText(r.body), 'err');
    say(`${e.displayName}: ${checkedIn ? 'sjekket inn' : 'innsjekkingen er angret'}. ${r.body.summary.checkedInSeats} kommet.`, 'ok');
    void page.reload(); onChanged();
  };
  const promote = async (e: Body) => {
    const r = await post(`${base}/${encodeURIComponent(e.id)}/promote`, { version: e.version });
    if (!r.ok) { void page.reload(); return say(errText(r.body), r.status === 409 ? 'warn' : 'err'); }
    say(`${e.displayName} er flyttet fra ventelisten til påmeldt.`, 'ok');
    void page.reload(); onChanged();
  };
  if (page.error) return <LoadError error={page.error} retry={page.reload} />;
  if (!page.data) return <Loading what="Henter listen …" />;
  const can: Body = page.data.can || {};
  return (
    <section className="work-comments" aria-label="Hvem kommer">
      {page.items.length === 0 && <p className="work-empty">Ingen på listen ennå.</p>}
      <ul className="reg-people">{page.items.map((e) => {
        const shown = e.id in pending ? pending[e.id] : Boolean(e.checkedIn);
        return (
          <li key={e.id} className={'reg-person' + (e.id in pending ? ' pending' : '')}>
            <span className="reg-name">
              <b><bdi dir="auto">{e.displayName}</bdi>{e.partySize > 1 && <span className="note"> · {e.partySize} personer</span>}</b>
              <span className="work-row-meta">
                {e.status === 'waitlisted' ? <Tag tone="gold">Venteliste nr. {e.queuePosition}</Tag> : <Tag tone="green">Påmeldt</Tag>}
                {e.byAccount && <Tag tone="grey">Fra appen</Tag>}
                {shown && <Tag tone="green">Kommet</Tag>}
              </span>
            </span>
            {can.manage && e.status === 'waitlisted' && (
              <Btn ghost onClick={() => promote(e)} aria-label={`Flytt ${e.displayName} fra ventelisten til påmeldt`}>Flytt opp</Btn>
            )}
            {can.checkin && e.status === 'registered' && (
              <Btn className={'btn sm reg-checkin' + (shown ? ' ghost' : '')} disabled={e.id in pending} aria-pressed={shown}
                   aria-label={`${shown ? 'Angre innsjekking for' : 'Sjekk inn'} ${e.displayName}`} onClick={() => checkIn(e, !shown)}>
                {shown ? 'Angre' : 'Sjekk inn'}
              </Btn>
            )}
          </li>
        );
      })}</ul>
      <MoreButton list={page} />
      <Msg text={msg.text} tone={msg.tone} />
    </section>
  );
}

/** «Påmelding»'s card for the Ramadan lists: absent unless the mosque has the Ramadan sign-up. */
export function RamadanListsCard() {
  return (
    <Card title="Ramadan: iftar og sehri" hint="Én liste per ramadandag, med antall og innsjekking. Innstillingene og «Opprett lister» er på siden «Ramadan».">
      <RamadanLists />
    </Card>
  );
}
